/**
 * EventDescriptionParser
 *
 * Parses and serializes event descriptions containing mixed text and checkbox items.
 * Simpler than MarkdownParser (used for tasklists) - no title requirement.
 *
 * Supported checkbox patterns:
 * - [ ] Task text - Unchecked
 * - [x] Task text - Checked
 * - [X] Task text - Checked (case-insensitive)
 * - - [ ] Task text - With dash prefix (for compatibility)
 * - - [x] Task text - Checked with dash
 */

export type ChecklistItem = {
  id: string;
  text: string;
  completed: boolean;
  lineIndex: number;
};

export type ParsedDescription = {
  textLines: string[];
  checklistItems: ChecklistItem[];
};

class EventDescriptionParserClass {
  // Supports: [ ], [x], [X], [v], [V], [], [  ] (double space)
  private static readonly CHECKBOX_REGEX = /^-?\s*\[([ xXvV]?| {2})\]\s*(.*)$/;

  /**
   * Parse event description into text lines and checklist items
   */
  parse(description: string): ParsedDescription {
    if (!description || description.trim() === '') {
      return { textLines: [], checklistItems: [] };
    }

    const lines = description.split('\n');
    const textLines: string[] = [];
    const checklistItems: ChecklistItem[] = [];

    lines.forEach((line, index) => {
      const match = line.match(EventDescriptionParserClass.CHECKBOX_REGEX);

      if (match) {
        const [, checkboxState, taskText] = match;
        const text = taskText.trim();
        const normalizedState = checkboxState.trim().toLowerCase();
        // Checked if 'x' or 'v', unchecked if empty/space/double-space
        const isCompleted = normalizedState === 'x' || normalizedState === 'v';
        checklistItems.push({
          id: this.generateId(text, index),
          text,
          completed: isCompleted,
          lineIndex: index,
        });
      } else if (line.trim()) {
        textLines.push(line);
      }
    });

    return { textLines, checklistItems };
  }

  /**
   * Serialize text lines and checklist items back to markdown
   */
  serialize(textLines: string[], items: ChecklistItem[]): string {
    const parts: string[] = [];

    if (textLines.length > 0) {
      parts.push(...textLines);
    }

    items.forEach(item => {
      const checkbox = item.completed ? '[x]' : '[ ]';
      parts.push(`${checkbox} ${item.text}`);
    });

    return parts.join('\n');
  }

  /**
   * Update a specific checklist item's completion state
   */
  updateItemCompletion(
    description: string,
    itemId: string,
    completed: boolean
  ): string {
    const parsed = this.parse(description);
    const item = parsed.checklistItems.find(i => i.id === itemId);

    if (!item) {
      return description;
    }

    item.completed = completed;
    return this.serialize(parsed.textLines, parsed.checklistItems);
  }

  /**
   * Update a specific checklist item's text
   */
  updateItemText(
    description: string,
    itemId: string,
    newText: string
  ): string {
    const parsed = this.parse(description);
    const item = parsed.checklistItems.find(i => i.id === itemId);

    if (!item) {
      return description;
    }

    item.text = newText;
    return this.serialize(parsed.textLines, parsed.checklistItems);
  }

  /**
   * Add a new checklist item
   */
  addItem(description: string, text: string): string {
    const parsed = this.parse(description);
    const newLineIndex = parsed.checklistItems.length;
    parsed.checklistItems.push({
      id: this.generateId(text, newLineIndex),
      text,
      completed: false,
      lineIndex: -1,
    });
    return this.serialize(parsed.textLines, parsed.checklistItems);
  }

  /**
   * Remove a checklist item
   */
  removeItem(description: string, itemId: string): string {
    const parsed = this.parse(description);
    const filteredItems = parsed.checklistItems.filter(i => i.id !== itemId);
    return this.serialize(parsed.textLines, filteredItems);
  }

  /**
   * Generate stable ID for checklist items based on content and position
   * This ensures the same checkbox text at the same position always gets the same ID
   */
  private generateId(text: string, lineIndex: number): string {
    // Create a simple hash from text and line index for stable IDs
    const hash = this.simpleHash(`${text}_${lineIndex}`);
    return `task_${hash}`;
  }

  /**
   * Simple string hash function for generating stable IDs
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }
}

export const EventDescriptionParser = new EventDescriptionParserClass();
